import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import supabase from "../../utils/supabase";

export default function AuthCallback() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [authMessage, setAuthMessage] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Get the current session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        console.error("Session error:", error);
        setError("Failed to get authentication session");
        setLoading(false);
        return;
      }

      if (session) {
        setAuthMessage("Authentication successful!");

        // Store user data
        const user = session.user;
        const userData = {
          id: user.id,
          email: user.email,
          first_name: user.user_metadata?.first_name || "",
          last_name: user.user_metadata?.last_name || "",
          role: user.app_metadata?.role || "student",
          school: user.user_metadata?.school,
          grade: user.user_metadata?.grade,
        };

        // Check if this is an LTI user
        const authProvider = localStorage.getItem("authProvider");
        const ltiUserData = localStorage.getItem("ltiUserData");

        // Redirect based on user role and context
        setTimeout(() => {
          // Check if there's a saved return path for LTI users
          const savedReturnPath = localStorage.getItem("ltiReturnPath");

          if (authProvider === "lti" && ltiUserData) {
            // For LTI users, check if we have a saved return path
            if (savedReturnPath) {
              // Navigate back to the original path
              navigate(savedReturnPath, { replace: true });
              // Clean up the saved path
              localStorage.removeItem("ltiReturnPath");
              return;
            } else {
              // Fallback to default LTI behavior
              const currentPath = window.location.pathname;
              if (currentPath.includes("/lti/")) {
                // Already on LTI page, just stay there
                return;
              } else {
                // Redirect to LTI resources page with preserved parameters
                const ltiToken = localStorage.getItem("ltiToken");

                const params = new URLSearchParams();
                if (ltiToken) params.set("ltik", ltiToken);

                const resourcesUrl = `/lti/resources?${params.toString()}`;
                navigate(resourcesUrl, { replace: true });
              }
            }
          } else if (userData.role === "teacher") {
            navigate("/teacher/dashboard", { replace: true });
          } else if (userData.role === "student") {
            navigate("/dashboard", { replace: true });
          } else {
            navigate("/", { replace: true });
          }
        }, 2000);
      } else {
        setError("No authentication session found");
        setLoading(false);
      }
    });
  }, [navigate]);

  if (loading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Authenticating...</p>
          <p className="text-gray-400 text-sm mt-2">
            Please wait while we verify your credentials
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <strong className="font-bold">Authentication Error</strong>
            <p className="mt-2">{error}</p>
          </div>
          <button
            onClick={() => navigate("/login-portal")}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded transition-colors"
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  if (authMessage) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            <strong className="font-bold">Success!</strong>
            <p className="mt-2">{authMessage}</p>
          </div>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Redirecting you to your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen w-screen items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600 text-lg">Processing...</p>
      </div>
    </div>
  );
}
